import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RiCalendarEventLine, RiEyeLine } from "react-icons/ri";
import { FaChartLine, FaDollarSign, FaEdit } from "react-icons/fa";
import { BiReceipt } from "react-icons/bi";
import { GetUserOwnEvents, DeleteEvent, UpdateEvent } from "../../../redux/actions/Events";
import { GetUserStats } from "../../../redux/actions/User";
import { ApiURL } from "../../../setting/GlobleVariable";

const UserDashboard = ({ setActive }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const eventsLoading = useSelector((state) => state.events.userCreatedEventsLoading);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalEvents: 0,
    totalRevenue: 0,
    totalTicketsSold: 0,
    totalViews: 0
  });

  const handleDeleteEvent = async (eventId, eventName) => {
    if (window.confirm(`Are you sure you want to delete "${eventName}"? This action cannot be undone.`)) {
      const result = await dispatch(DeleteEvent(eventId));
      if (result.success) {
        // Refresh the events list
        const eventsResult = await dispatch(GetUserOwnEvents());
        if (eventsResult.success && eventsResult.events) {
          setEvents(eventsResult.events);
          
          // Recalculate stats
          const totalRevenue = eventsResult.events.reduce((sum, event) => sum + (event.totalRevenue || 0), 0);
          const totalTicketsSold = eventsResult.events.reduce((sum, event) => sum + (event.ticketsSold || 0), 0);
          const totalViews = eventsResult.events.reduce((sum, event) => sum + (event.views || 0), 0);
          
          setStats({
            totalEvents: eventsResult.events.length,
            totalRevenue,
            totalTicketsSold,
            totalViews
          });
        }
      }
    }
  };

  const handleEditEvent = (eventId) => {
    // Navigate to edit page
    window.open(`/edit-event/${eventId}`, '_blank');
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      // Fetch user's own events
      const eventsResult = await dispatch(GetUserOwnEvents());
      if (eventsResult.success && eventsResult.events) {
        setEvents(eventsResult.events);
        
        // Calculate stats from events (these now have correct inventory data from DB)
        const totalRevenue = eventsResult.events.reduce((sum, event) => sum + (event.totalRevenue || 0), 0);
        const totalTicketsSold = eventsResult.events.reduce((sum, event) => sum + (event.ticketsSold || 0), 0);
        const totalViews = eventsResult.events.reduce((sum, event) => sum + (event.views || 0), 0);
        
        setStats({
          totalEvents: eventsResult.events.length,
          totalRevenue,
          totalTicketsSold,
          totalViews
        });
      }
      
      // Also fetch stats from dedicated endpoint (more accurate with monthly data)
      const statsResult = await dispatch(GetUserStats());
      if (statsResult.success && statsResult.stats) {
        setStats(prev => ({
          ...prev,
          totalEvents: statsResult.stats.totalEvents || prev.totalEvents,
          totalRevenue: statsResult.stats.totalRevenue || prev.totalRevenue,
          totalTicketsSold: statsResult.stats.totalTicketsSold || prev.totalTicketsSold,
          totalViews: statsResult.stats.totalViews || prev.totalViews
        }));
      }
      
      setLoading(false);
    };
    
    fetchData();
  }, [dispatch]);

  if (loading) {
    return (
      <div className="py-2 px-2 bg-color1 text-color2 h-auto overflow-y-auto">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-color2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-2 px-2 bg-color1 text-color2 h-auto overflow-y-auto profile_AccountDetails">
      <h2 className="text-[25px] py-2 px-2 font-bold">My Dashboard</h2>
      
      {/* Welcome Section */}
      <div className="px-2 sm:px-5 mb-6">
        <div className="w-[100%] my-3 flex justify-start place-items-center border-b-[1px] border-b-redColor pb-3">
          <div>
            <h3 className="text-[20px] font-medium">Welcome back, {user?.firstName || 'User'}!</h3>
            <p className="text-color2 opacity-80">Here's an overview of your event management activities</p>
           
            
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="px-2 sm:px-5 mb-6">
        <h3 className="text-[20px] py-2 px-2 font-medium mb-4">Statistics Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Total Events */}
          <div className="w-full h-[120px] cursor-pointer shadow-md rounded-md border-[1px] border-color4 py-3 px-3 flex flex-col justify-center place-items-center gap-2 bg-color4">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg mb-2">
              <RiCalendarEventLine className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-[24px] font-bold text-blue-600">{stats.totalEvents}</div>
            <div className="text-[14px] text-center">Total Events</div>
          </div>

          {/* Total Revenue */}
          <div className="w-full h-[120px] cursor-pointer shadow-md rounded-md border-[1px] border-color4 py-3 px-3 flex flex-col justify-center place-items-center gap-2 bg-color4">
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg mb-2">
              <FaDollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-[24px] font-bold text-green-600">
              ${stats.totalRevenue.toLocaleString()}
            </div>
            <div className="text-[14px] text-center">Total Revenue</div>
          </div>

          {/* Tickets Sold */}
          <div className="w-full h-[120px] cursor-pointer shadow-md rounded-md border-[1px] border-color4 py-3 px-3 flex flex-col justify-center place-items-center gap-2 bg-color4">
            <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg mb-2">
              <BiReceipt className="w-6 h-6 text-purple-600" />
            </div>
            <div className="text-[24px] font-bold text-purple-600">{stats.totalTicketsSold}</div>
            <div className="text-[14px] text-center">Tickets Sold</div>
          </div>

          {/* Total Views */}
          {/* <div className="w-full h-[120px] cursor-pointer shadow-md rounded-md border-[1px] border-color4 py-3 px-3 flex flex-col justify-center place-items-center gap-2 bg-color4">
            <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-lg mb-2">
              <RiEyeLine className="w-6 h-6 text-orange-600" />
            </div>
            <div className="text-[24px] font-bold text-orange-600">
              —
            </div>
            <div className="text-[14px] text-center">Total Views (not tracked yet)</div>
          </div> */}
        </div>
      </div>

      {/* My Events */}
      <div className="px-2 sm:px-5">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-[20px] py-2 px-2 font-medium">My Events ({events.length})</h3>
          <button
            onClick={() => window.open('/create-event', '_blank')}
            className="px-4 py-2 bg-redColor text-white rounded-md text-[14px] font-medium hover:opacity-90"
          >
            + Create New Event
          </button>
        </div>

        {loading ? (
          <div className="w-full shadow-md rounded-md border-[1px] border-color4 py-8 px-4 bg-color4 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-redColor mx-auto mb-4"></div>
            <h3 className="text-[16px] font-medium">Loading events...</h3>
          </div>
        ) : events.length > 0 ? (
          <div className="space-y-4 max-h-[500px] overflow-y-auto">
            {events.map((event, index) => (
              <div key={event._id || index} className="w-full shadow-md rounded-md border-[1px] border-color4 py-4 px-4 bg-color4 hover:border-redColor transition-colors">
                {/* Event Header */}
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div className="flex items-center space-x-4">
                    <img
                      className="h-14 w-14 rounded object-cover"
                      src={event.image ? `${ApiURL.replace('/api', '')}/uploads/${event.image}` : '/default-event.jpg'}
                      alt={event.name || event.eventName}
                      onError={(e) => { e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjOWNhM2FmIi8+Cjx0ZXh0IHg9IjIwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iI2ZmZmZmZiIgZm9udC1zaXplPSIxNiIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIj5FdmVudDwvdGV4dD4KPHN2Zz4='; }}
                    />
                    <div>
                      <div className="text-[16px] font-medium">{event.name || event.eventName || 'Unnamed Event'}</div>
                      <div className="text-[14px] opacity-80">{event.venue?.name || event.venue?.location || event.location || 'No Location'}</div>
                      <div className="text-[12px] opacity-60">
                        {event.occurs_at ? new Date(event.occurs_at).toLocaleDateString('en-US', { dateStyle: 'medium' }) : 'No Date Set'}
                      </div>
                    </div>
                  </div>
                  <div className="text-right min-w-[150px]">
                    <div className="text-[16px] font-medium text-green-500">
                      ${(event.totalRevenue || 0).toLocaleString()}
                    </div>
                    <div className="text-[12px] opacity-80">Total Revenue</div>
                    <div className="text-[12px] opacity-60">
                      {event.ticketsSold || 0} / {event.totalTickets || 0} total sold
                    </div>
                    {event.ticketsSold === 0 && (
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() => handleEditEvent(event._id)}
                          className="px-3 py-1 bg-blue-600 text-white text-[12px] rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
                        >
                          <FaEdit className="w-3 h-3" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteEvent(event._id, event.name || event.eventName)}
                          className="px-3 py-1 bg-red-600 text-white text-[12px] rounded hover:bg-red-700 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Per-Ticket-Type Stats */}
                {event.userTickets && event.userTickets.length > 0 && (
                  <div className="border-t border-color4 pt-3 mt-2">
                    <div className="text-[12px] font-medium opacity-70 mb-2">Ticket Types:</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {event.userTickets.map((ticket, idx) => {
                        const quantity = parseInt(ticket.quantity) || 0;
                        const sold = ticket.sold || 0;
                        const remaining = ticket.remaining !== undefined ? ticket.remaining : (quantity - sold);
                        const percentSold = quantity > 0 ? Math.round((sold / quantity) * 100) : 0;
                        
                        return (
                          <div key={ticket.id || idx} className="bg-color1 rounded px-3 py-2">
                            <div className="flex justify-between items-center">
                              <span className="text-[13px] font-medium">{ticket.ticketName}</span>
                              <span className="text-[11px] text-green-500">${ticket.price}</span>
                            </div>
                            <div className="text-[11px] opacity-80 mt-1">
                              {sold} / {quantity} sold ({percentSold}%)
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-1.5 mt-1">
                              <div 
                                className="bg-redColor h-1.5 rounded-full transition-all" 
                                style={{ width: `${percentSold}%` }}
                              ></div>
                            </div>
                            <div className="text-[10px] opacity-60 mt-1">
                              {remaining} remaining
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full shadow-md rounded-md border-[1px] border-color4 py-8 px-4 bg-color4 text-center">
            <RiCalendarEventLine className="mx-auto h-12 w-12 opacity-50 mb-4" />
            <h3 className="text-[16px] font-medium mb-2">No events yet</h3>
            <p className="text-[14px] opacity-80 mb-4">
              Get started by creating your first event.
            </p>
            <button
              onClick={() => window.open('/create-event', '_blank')}
              className="px-4 py-2 bg-redColor text-white rounded-md text-[14px] font-medium hover:opacity-90"
            >
              Create Event
            </button>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      {/* <div className="px-2 sm:px-5 mt-6">
        <h3 className="text-[20px] py-2 px-2 font-medium mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => setActive && setActive(1)}
            className="px-4 py-2 bg-color4 border-[1px] border-color4 text-color2 rounded-md text-[14px] font-medium hover:bg-color1 transition-colors"
          >
            <RiCalendarEventLine className="inline w-4 h-4 mr-2" />
            My Tickets
          </button>
          <button
            onClick={() => window.open('/user/dashboard', '_blank')}
            className="px-4 py-2 bg-color4 border-[1px] border-color4 text-color2 rounded-md text-[14px] font-medium hover:bg-color1 transition-colors"
          >
            <FaChartLine className="inline w-4 h-4 mr-2" />
            Full Dashboard
          </button>
          <button
            onClick={() => window.open('/create-event', '_blank')}
            className="px-4 py-2 bg-redColor text-white rounded-md text-[14px] font-medium hover:opacity-90 transition-colors"
          >
            <RiCalendarEventLine className="inline w-4 h-4 mr-2" />
            Create New Event
          </button>
        </div>
      </div> */}
    </div>
  );
};

export default UserDashboard;